import { uniq } from 'lodash/fp';
import { Fragment, ReactNode, Ref, useMemo } from 'react';

import {
  buildStepWidths,
  PathHighlightInformation,
  PathIndex,
  StepFlowBottomSingleLine,
  StepFlowBranchConnectors,
  StepFlowBranchContainer,
  StepFlowBranchEmptySpacer,
  StepFlowBranchStepsContainer,
  StepFlowBranchSubTree,
  StepFlowContainer,
  StepFlowStepContainer,
  StepFlowVerticalLine,
  StepPosition,
} from 'dashboard/components/StepFlow/StepFlowElements';
import { WorkflowStepType } from 'dashboard/models/workflow-version';
import { BASE } from 'design-system/variables/spacing';

// Abstract interface of common props between StepFlow and StepAndConnector
interface StepFlowConsistentProps<Step> {
  stepContentWidth: number;
  renderStep: (step: Step) => ReactNode;
  renderConnector: (previousStep: Step, nextStep: Step) => ReactNode;
  renderBranchRootConnector: (branchRootStep: Step, branchIndex: number) => ReactNode;
  innerStyle?: {
    blurBranchContainer?: (branchRootStep: Step) => boolean;
    pathHighlightInformationForStep?: (step: Step) => PathHighlightInformation;
  };
  splitBranches?: boolean;
  withOutputStep?: boolean;
  withMinWidth?: boolean;
  collapsedNodeTokens?: string[];
  childlessRunNodeTokens?: string[];
}
interface StepFlowProps<Step> extends StepFlowConsistentProps<Step> {
  steps: Step[];
  innerRef: Ref<HTMLDivElement>;
}

/**
 * A component to render steps with tree-ish layout
 *
 * @param innerRef - The ref for the root container. Couldn't use React.forwardRef instead to avoid unintuitive Generic wrapping
 * @param steps - The list of steps that will be walked iteratively.
 * Expects each element has an unique `token`, and optional `branches` which has child steps
 * @param stepContentWidth - The width of a single rendered step
 * @param renderStep - The function to render each step that takes `step` as an argument
 * @param renderConnector - The function to render a connector between steps that takes "previous step" and "next step"
 * "next step" will be `undefined` when doesn't exist
 * @param renderBranchRootConnector - The function to render a connector on the root of a branch that takes "step" and "branch index"
 * @param innerStyle - The object that owns functions to change visibility
 */
const StepFlow = <Step extends { token: string; branches?: Step[][]; branch?: Step[] }>({
  innerRef,
  innerStyle,
  steps,
  stepContentWidth,
  renderStep,
  renderConnector,
  renderBranchRootConnector,
  splitBranches,
  withOutputStep = true,
  withMinWidth = true,
  collapsedNodeTokens = [],
  childlessRunNodeTokens = [],
}: StepFlowProps<Step>) => {
  const [stepTokenToPositionMap, totalWidth]: [Map<string, StepPosition>, number] = useMemo(() => {
    const map = new Map<string, StepPosition>();
    const widthOfTree = buildStepWidths(
      steps,
      stepContentWidth,
      map,
      uniq([...collapsedNodeTokens, ...childlessRunNodeTokens]),
    );
    return [map, widthOfTree];
  }, [steps, stepContentWidth, collapsedNodeTokens, childlessRunNodeTokens]);

  return (
    <StepFlowContainer withMinWidth={withMinWidth} width={totalWidth} ref={innerRef}>
      {steps.map((step, i) => (
        <StepAndConnector
          key={i}
          step={step}
          nextStep={steps[i + 1]}
          renderStep={renderStep}
          renderConnector={renderConnector}
          renderBranchRootConnector={renderBranchRootConnector}
          splitBranches={splitBranches}
          withOutputStep={withOutputStep}
          innerStyle={innerStyle}
          stepContentWidth={stepContentWidth}
          stepTokenToPositionMap={stepTokenToPositionMap}
          collapsedNodeTokens={collapsedNodeTokens}
          childlessRunNodeTokens={childlessRunNodeTokens}
        />
      ))}
    </StepFlowContainer>
  );
};

interface StepAndConnectorProps<Step> extends StepFlowConsistentProps<Step> {
  stepTokenToPositionMap: Map<string, StepPosition>;
  step: Step;
  nextStep: Step;
}

const StepAndConnector = <
  Step extends { token: string; type?: string; branches?: Step[][]; branch?: Step[] },
>(
  props: StepAndConnectorProps<Step>,
) => {
  const {
    renderStep,
    renderConnector,
    renderBranchRootConnector,
    step,
    nextStep,
    stepContentWidth,
    stepTokenToPositionMap,
    innerStyle,
    splitBranches,
    collapsedNodeTokens,
    childlessRunNodeTokens,
    withOutputStep = true,
  } = props;
  if (step == null) {
    return null;
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const width = stepTokenToPositionMap.get(step.token).width;

  const isLastConditionalJoin =
    !withOutputStep && step.type === WorkflowStepType.Conditional && !nextStep;

  const {
    toOutBranchLineColor,
    connectorAfterStepColor,
    leftBranchOutConnectorsColor,
    rightBranchOutConnectorsColor,
    rightBranchInConnectorsColor,
    leftBranchInConnectorsColor,
    pathToChildInIndex,
    pathToChildOutIndex,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  } = innerStyle?.pathHighlightInformationForStep(step) ?? {};

  const hasSingleBranch = !!(Array.isArray(step.branch) && step.branch.length);
  const childTokens = [
    ...(Array.isArray(step.branches)
      ? step.branches.map((_, i) => `${step.token}-branch-${i}`)
      : []),
    ...(hasSingleBranch ? [`${step.token}-branch-${0}`, `${step.token}-branch-${1}`] : []),
  ];

  const connectorAfterStep = () => {
    if (step.type === WorkflowStepType.Output || isLastConditionalJoin) {
      return null;
    }

    if (splitBranches) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (step.branches?.length > 0 || !nextStep) {
        return <></>;
      }
    }

    return (
      renderConnector(step, nextStep) ?? (
        <StepFlowBranchSubTree>
          <StepFlowBottomSingleLine color={connectorAfterStepColor} />
        </StepFlowBranchSubTree>
      )
    );
  };

  const branches = [
    ...(Array.isArray(step.branches) ? step.branches : []),
    ...(hasSingleBranch ? [[], step.branch] : []),
  ];

  const showChildren =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    !collapsedNodeTokens.includes(step.token) && !childlessRunNodeTokens.includes(step.token);

  return (
    <StepFlowStepContainer width={width}>
      {/* current step */}
      {renderStep(step)}

      {/* branches under a current step */}
      {(branches.length ?? 0) > 0 && showChildren && (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <StepFlowBranchContainer blur={innerStyle?.blurBranchContainer(step) ?? false}>
          <StepFlowBottomSingleLine color={toOutBranchLineColor} />
          <StepFlowBranchConnectors
            branchDirection='out'
            childTokens={childTokens}
            stepTokenToPositionMap={stepTokenToPositionMap}
            width={stepContentWidth}
            leftPathColor={leftBranchOutConnectorsColor}
            rightPathColor={rightBranchOutConnectorsColor}
            pathToChildIndex={pathToChildOutIndex}
          />
          <StepFlowBranchStepsContainer>
            {branches.map((branchSteps, branchIndex) => {
              const branchRootConnector = renderBranchRootConnector(step, branchIndex);
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              const spaceWidth = branchSteps[0]
                ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  stepTokenToPositionMap.get(branchSteps[0].token).width
                : stepContentWidth + BASE * 2;

              return (
                <Fragment key={branchIndex}>
                  {branchRootConnector && (
                    <StepFlowBranchSubTree key={branchIndex}>
                      {!(hasSingleBranch && branchIndex === 0) && branchRootConnector}
                      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                      {branchSteps.length > 0 ? (
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        branchSteps.map((step, i) => (
                          <StepAndConnector
                            key={i}
                            step={step}
                            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                            nextStep={branchSteps[i + 1]}
                            renderStep={renderStep}
                            renderConnector={renderConnector}
                            renderBranchRootConnector={renderBranchRootConnector}
                            splitBranches={splitBranches}
                            withOutputStep={withOutputStep}
                            innerStyle={innerStyle}
                            stepContentWidth={stepContentWidth}
                            stepTokenToPositionMap={stepTokenToPositionMap}
                            collapsedNodeTokens={collapsedNodeTokens}
                            childlessRunNodeTokens={childlessRunNodeTokens}
                          />
                        ))
                      ) : (
                        <StepFlowBranchEmptySpacer width={spaceWidth} />
                      )}
                      {withOutputStep && (
                        <StepFlowVerticalLine
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          color={
                            hasSingleBranch
                              ? leftBranchInConnectorsColor
                              : pathToChildInIndex === branchIndex ||
                                  pathToChildInIndex === PathIndex.All
                                ? connectorAfterStepColor
                                : null
                          }
                        />
                      )}
                    </StepFlowBranchSubTree>
                  )}
                </Fragment>
              );
            })}
          </StepFlowBranchStepsContainer>
          {!splitBranches && !isLastConditionalJoin && (
            <StepFlowBranchConnectors
              branchDirection='in'
              childTokens={childTokens}
              stepTokenToPositionMap={stepTokenToPositionMap}
              width={stepContentWidth}
              leftPathColor={leftBranchInConnectorsColor}
              rightPathColor={rightBranchInConnectorsColor}
              pathToChildIndex={pathToChildInIndex}
            />
          )}
        </StepFlowBranchContainer>
      )}

      {connectorAfterStep()}
    </StepFlowStepContainer>
  );
};

export default StepFlow;
